import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

interface OrphanInstance {
  instance_id: string;
  provider: string;
  region: string;
  instance_type: string;
  state: string;
  launch_time: string | null;
  running_hours: number;
  category: string;
  estimated_cost_per_hour: number;
  tags: Record<string, string>;
}

interface OrphanScanResult {
  scanned_at: string;
  orphans: OrphanInstance[];
  summary: {
    total: number;
    stale_builder: number;
    active_builder: number;
    user_orphan: number;
    pre_validator: number;
    unknown: number;
    estimated_hourly_waste: number;
  };
  errors: string[];
}

const CATEGORY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  stale_builder: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Stale Builder' },
  user_orphan: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'User Orphan' },
  active_builder: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Active Builder' },
  pre_validator: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pre-Validator' },
  unknown: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Unknown' },
};

export default function AdminOrphanInstancesPage() {
  const [data, setData] = useState<OrphanScanResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmTerminate, setConfirmTerminate] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchOrphans();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrphans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${getApiUrl()}/admin/orphan-instances`, { headers });
      setData(res.data);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 403) setError('Access denied. Admin privileges required.');
      else if (err.response?.status === 401) navigate('/login');
      else setError('Failed to load orphan data');
    } finally {
      setLoading(false);
    }
  };

  const refreshScan = async () => {
    try {
      setRefreshing(true);
      const res = await axios.post(`${getApiUrl()}/admin/orphan-instances/refresh`, {}, { headers });
      setData(res.data);
    } catch {
      setError('Failed to refresh scan');
    } finally {
      setRefreshing(false);
    }
  };

  const terminateOrphan = async (orphan: OrphanInstance) => {
    try {
      setActionLoading(orphan.instance_id);
      await axios.post(
        `${getApiUrl()}/admin/orphan-instances/${orphan.instance_id}/terminate?provider=${orphan.provider}&region=${orphan.region}`,
        {},
        { headers },
      );
      // Remove from local state
      if (data) {
        const filtered = data.orphans.filter(o => o.instance_id !== orphan.instance_id);
        setData({
          ...data,
          orphans: filtered,
          summary: {
            ...data.summary,
            total: filtered.length,
            [orphan.category]: Math.max(0, (data.summary[orphan.category as keyof typeof data.summary] as number) - 1),
            estimated_hourly_waste: +(data.summary.estimated_hourly_waste - orphan.estimated_cost_per_hour).toFixed(4),
          },
        });
      }
      setConfirmTerminate(null);
    } catch {
      setError(`Failed to terminate ${orphan.instance_id}`);
    } finally {
      setActionLoading(null);
    }
  };

  const dismissOrphan = async (orphan: OrphanInstance) => {
    try {
      setActionLoading(orphan.instance_id);
      await axios.post(`${getApiUrl()}/admin/orphan-instances/${orphan.instance_id}/dismiss`, {}, { headers });
      if (data) {
        const filtered = data.orphans.filter(o => o.instance_id !== orphan.instance_id);
        setData({
          ...data,
          orphans: filtered,
          summary: {
            ...data.summary,
            total: filtered.length,
            [orphan.category]: Math.max(0, (data.summary[orphan.category as keyof typeof data.summary] as number) - 1),
            estimated_hourly_waste: +(data.summary.estimated_hourly_waste - orphan.estimated_cost_per_hour).toFixed(4),
          },
        });
      }
    } catch {
      setError(`Failed to dismiss ${orphan.instance_id}`);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Orphan Instances</h1>
            <p className="text-gray-400 text-sm mt-1">
              Cloud instances with PropAlgos tags that have no database record
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={refreshScan}
              disabled={refreshing}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              {refreshing && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
              {refreshing ? 'Scanning...' : 'Refresh Scan'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <p className="text-gray-400 text-xs uppercase tracking-wide">Total Orphans</p>
              <p className="text-2xl font-bold text-white mt-1">{data.summary.total}</p>
            </div>
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <p className="text-gray-400 text-xs uppercase tracking-wide">Est. Hourly Waste</p>
              <p className="text-2xl font-bold text-amber-400 mt-1">${data.summary.estimated_hourly_waste.toFixed(2)}</p>
            </div>
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <p className="text-gray-400 text-xs uppercase tracking-wide">Stale Builders</p>
              <p className="text-2xl font-bold text-red-400 mt-1">{data.summary.stale_builder}</p>
            </div>
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <p className="text-gray-400 text-xs uppercase tracking-wide">User Orphans</p>
              <p className="text-2xl font-bold text-red-400 mt-1">{data.summary.user_orphan}</p>
            </div>
          </div>
        )}

        {/* Scan metadata */}
        {data && (
          <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
            <span>Last scanned: {new Date(data.scanned_at).toLocaleString()}</span>
            {data.errors.length > 0 && (
              <span className="text-amber-500">{data.errors.length} scan error{data.errors.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        )}

        {/* Errors */}
        {data && data.errors.length > 0 && (
          <div className="mb-4 bg-amber-900/20 border border-amber-800/50 rounded-lg p-3">
            <p className="text-amber-300 text-xs font-medium mb-1">Scan Errors</p>
            {data.errors.map((e, i) => (
              <p key={i} className="text-amber-400/70 text-xs">{e}</p>
            ))}
          </div>
        )}

        {/* Table */}
        {data && data.orphans.length === 0 ? (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-12 text-center">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-300 font-medium">No orphan instances detected</p>
            <p className="text-gray-500 text-sm mt-1">All PropAlgos-tagged instances match database records</p>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-gray-800">
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Provider</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Instance ID</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Type</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Region</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">State</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Running</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Category</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Est. $/hr</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {data?.orphans.map((orphan) => {
                    const catStyle = CATEGORY_STYLES[orphan.category] || CATEGORY_STYLES.unknown;
                    const isActionTarget = orphan.category === 'stale_builder' || orphan.category === 'user_orphan';
                    return (
                      <tr key={orphan.instance_id} className={`hover:bg-gray-800/30 ${isActionTarget ? 'bg-red-900/5' : ''}`}>
                        <td className="px-4 py-3 text-sm">
                          <span className="text-gray-300 font-medium uppercase">{orphan.provider}</span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="text-gray-200 font-mono text-xs">{orphan.instance_id}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">{orphan.instance_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">{orphan.region}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
                            {orphan.state}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">{orphan.running_hours.toFixed(1)}h</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${catStyle.bg} ${catStyle.text}`}>
                            {catStyle.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">${orphan.estimated_cost_per_hour.toFixed(3)}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-1.5">
                            {confirmTerminate === orphan.instance_id ? (
                              <>
                                <button
                                  onClick={() => terminateOrphan(orphan)}
                                  disabled={actionLoading === orphan.instance_id}
                                  className="px-2 py-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded text-xs font-medium transition-colors"
                                >
                                  {actionLoading === orphan.instance_id ? '...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setConfirmTerminate(null)}
                                  className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-xs transition-colors"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => setConfirmTerminate(orphan.instance_id)}
                                  className="px-2 py-1 bg-red-900/50 hover:bg-red-800/50 text-red-400 rounded text-xs font-medium transition-colors"
                                >
                                  Terminate
                                </button>
                                <button
                                  onClick={() => dismissOrphan(orphan)}
                                  disabled={actionLoading === orphan.instance_id}
                                  className="px-2 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-400 rounded text-xs transition-colors"
                                >
                                  Dismiss
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
